import { sedrApi } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';

export function showSedrCreateStarterSetItemDialog(options: {
  setId: string;
  onCreated: () => void | Promise<void>;
}): void {
  const shell = createModalShell({
    titleKey: 'sedr.requirement.createTitle',
    titleId: 'sedr-library-create-item-title',
    helpTopic: dialogHelpTopic('sedrCreateRequirement'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-library-create-item-form">
      <label>
        <span data-i18n="sedr.table.reqId"></span>
        <input name="requirementCode" required maxlength="64" autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.table.title"></span>
        <input name="title" required maxlength="500" autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.table.description"></span>
        <textarea name="description" maxlength="5000" rows="3"></textarea>
      </label>
      <label>
        <span data-i18n="sedr.requirement.category"></span>
        <input name="category" required maxlength="200" autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.table.owner"></span>
        <input name="ownerName" maxlength="200" autocomplete="off" />
      </label>
      <p class="error-text" id="sedr-library-create-item-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-library-create-item-form');
  submitButton.setAttribute('data-i18n', 'sedr.library.addItem');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-library-create-item-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-library-create-item-error') as HTMLParagraphElement;
  const codeInput = form.querySelector('input[name="requirementCode"]') as HTMLInputElement;
  const categoryInput = form.querySelector('input[name="category"]') as HTMLInputElement;
  categoryInput.value = 'Static Governance';
  let submitting = false;
  let lastErrorKey = 'status.error';

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  codeInput.focus();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;

    void (async () => {
      submitting = true;
      errorEl.hidden = true;
      submitButton.disabled = true;
      const data = new FormData(form);
      try {
        await sedrApi.createStarterSetItem(options.setId, {
          requirementCode: String(data.get('requirementCode') ?? '').trim(),
          title: String(data.get('title') ?? '').trim(),
          description: String(data.get('description') ?? '').trim() || null,
          category: String(data.get('category') ?? '').trim() || 'Static Governance',
          ownerName: String(data.get('ownerName') ?? '').trim() || null,
        });
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onCreated();
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        errorEl.hidden = false;
        errorEl.textContent = t(lastErrorKey);
      }
    })();
  });
}
